import { useEffect, useState } from "react"
import Loading from "../../../../common/components/ui/loading/Loading"
import GenericTable from "../../../../common/components/ui/table/GenericTable"
import { numberToCurrency } from "../../../../common/helpers/currency.helpers"
import { formatDate } from "../../../../common/helpers/date.helpers"
import { usePartnerDashboardContext } from "../partner-dashboard/usePartnerDashboardContext"
import { PAYMENT_STATUS, PaymentInfo } from "../../../../types/payment.type";
import "./PaymentsTable.block.scss"
import { Modal } from "flowbite-react"
import { getPartnerPayments } from "../../../../common/services/partners.api"

const STATUS_LABELS = {
  [PAYMENT_STATUS.PENDING]: "Pendente",
  [PAYMENT_STATUS.RECEIVED]: "Recebido",
  [PAYMENT_STATUS.CONFIRMED]: "Confirmado",
  [PAYMENT_STATUS.OVERDUE]: "Atrasado",
  [PAYMENT_STATUS.REFUNDED]: "Reembolsado",
  [PAYMENT_STATUS.RECEIVED_IN_CASH]: "Recebido em dinheiro",
  [PAYMENT_STATUS.REFUND_REQUESTED]: "Reembolso solicitado",
  [PAYMENT_STATUS.REFUND_IN_PROGRESS]: "Reembolso em andamento",
  [PAYMENT_STATUS.CHARGEBACK_REQUESTED]: "Estorno solicitado",
  [PAYMENT_STATUS.CHARGEBACK_DISPUTE]: "Disputa de estorno",
  [PAYMENT_STATUS.AWAITING_CHARGEBACK_REVERSAL]: "Aguardando reversão de estorno",
  [PAYMENT_STATUS.DUNNING_REQUESTED]: "Cobrança solicitada",
  [PAYMENT_STATUS.DUNNING_RECEIVED]: "Cobrança recebida",
  [PAYMENT_STATUS.Inativo]: "Removida"
};

function getStatusLabels(status: string) {
  return STATUS_LABELS[status] || status;
}

export function PartnerPaymentsTable() {

  const { partner } = usePartnerDashboardContext()
  const [payments, setPayments] = useState<PaymentInfo[]>([])
  const [showFines, setShowFines] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const handleStatusChange = (status: string) => {
    setStatusFilter(status); // Define o filtro de status
    setShowFines(false); // Desativa o filtro de multas ao alterar o status
  };
  const [modalPayment, setModalPayment] = useState(false);
  const [totalPaidEnum, setTotalPaidEnum] = useState<number>(0);
  const [totalPendingEnum, setTotalPendingEnum] = useState<number>(0);
  const [totalOverdueEnum, setTotalOverdueEnum] = useState<number>(0);

  const tableHeaders = {
    "notes": "Descrição",
    "amount": "Valor",
    "dueDate": "Vencimento",
    "paymentDate": "Pagamento",
    "status": "Status",
    "externalId": "Link",
  };

  useEffect(() => {
    async function loadPayments(partneId: string) {
      if (!partneId || !partner.cnpj) return;

      const { data, ok } = await getPartnerPayments(partneId);

      if (ok) {
        // Resetar os totais antes de calcular novamente
        let totalPaid = 0;
        let totalPending = 0;
        let totalOverdue = 0;

        // Calcular os totais antes de filtrar
        data.forEach(payment => {
          if (payment.status === PAYMENT_STATUS.RECEIVED) {
            totalPaid += Number(payment["amount"]) || 0;
          } else if (payment.status === PAYMENT_STATUS.PENDING) {
            totalPending += Number(payment["amount"]) || 0;
          } else if (payment.status === PAYMENT_STATUS.OVERDUE) {
            totalOverdue += Number(payment["amount"]) || 0;
          }
        });

        // Atualizar os estados apenas uma vez, depois de todos os cálculos
        setTotalPaidEnum(totalPaid);
        setTotalPendingEnum(totalPending);
        setTotalOverdueEnum(totalOverdue);

        const filteredPayments = data.filter(payment => {
          if (showFines) {
            // Filtra pelos pagamentos de multas
            //TODO: Adicionar comparação pela descrição da fatura.
            const isTrafficFine =
              payment.planId?.match(/\b(multa|fine)\b/i) ||
              /AUTO|INFRAÇÃO|AUTO DE INFRAÇÃO/i.test(payment.description);

            // Se for multa, verifica se o status é PENDING
            return isTrafficFine && payment.status && ['PENDING', 'OVERDUE'].includes(payment.status);
          } else if (statusFilter === 'RECEIVED') {
            // Agrupa RECEIVED, CONFIRMED e RECEIVED_IN_CASH
            return payment.status && ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(payment.status);
          } else if (statusFilter) {
            // Filtro normal
            return payment.status === statusFilter;
          } else {
            return true; // Mostra todos os pagamentos se nenhum filtro estiver aplicado
          }
        });

        // Ordena os pagamentos pela data de vencimento
        const sortedPayments = filteredPayments.sort((a, b) => {
          const dateB = new Date(b.dueDate!);
          const dateA = new Date(a.dueDate!);
          return dateB.getTime() - dateA.getTime();
        });

        setPayments(sortedPayments as unknown as PaymentInfo[]);
      }
    }

    // Resetar pagamentos e totais antes de buscar novamente
    setPayments([]);
    setTotalPaidEnum(0);
    setTotalPendingEnum(0);
    setTotalOverdueEnum(0);

    if (partner._id) loadPayments(partner._id);
  }, [partner._id, partner.cnpj, showFines, statusFilter]);

  const withInactiveStyle = (
    content: React.ReactNode,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>
  ): React.ReactNode => {
    return data.status === PAYMENT_STATUS.Inativo
      ? <span className="text-gray">{content}</span>
      : content;
  };

  return (
    <div className="tabs-border-line">
      <section className="tabs-item-style">

        <section>
          <div id="top-table-payments" className="flex flex-col sm:flex-row justify-between items-center gap-y-4 sm:gap-x-4">
            {/* Título e Ícone */}
            <div className="flex items-center gap-x-2">
              <h2 className="text-2xl font-semibold">Pagamentos</h2>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mb-4 mt-4">
              {/* Todas */}
              <div className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  id="showPending"
                  className="text-blue-600 focus:ring-blue-500"
                  checked={statusFilter === '' && !showFines}
                  onChange={() => handleStatusChange('')}
                />
                <label htmlFor="showPending" className="ml-2 text-sm font-medium text-gray-700">
                  Todas
                </label>
              </div>

              {/* Pendente */}
              <div className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  id="showPending"
                  className="text-blue-600 focus:ring-blue-500"
                  checked={statusFilter === 'PENDING' && !showFines}
                  onChange={() => handleStatusChange('PENDING')}
                />
                <label htmlFor="showPending" className="ml-2 text-sm font-medium text-gray-700">
                  Pendente
                </label>
              </div>

              {/* Recebido */}
              <div className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  id="showReceived"
                  className="text-blue-600 focus:ring-blue-500"
                  checked={
                    ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(statusFilter) && !showFines
                  }
                  onChange={() => handleStatusChange('RECEIVED',)}
                />
                <label htmlFor="showReceived" className="ml-2 text-sm font-medium text-gray-700">
                  Recebido
                </label>
              </div>

              {/* Atrasado */}
              <div className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  id="showOverdue"
                  className="text-blue-600 focus:ring-blue-500"
                  checked={statusFilter === 'OVERDUE' && !showFines}
                  onChange={() => handleStatusChange('OVERDUE')}
                />
                <label htmlFor="showOverdue" className="ml-2 text-sm font-medium text-gray-700">
                  Atrasado
                </label>
              </div>

              {/* Inativo */}
              <div className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  id="showInactive"
                  className="text-blue-600 focus:ring-blue-500"
                  checked={statusFilter === 'Inativo' && !showFines}
                  onChange={() => handleStatusChange('Inativo')}
                />
                <label htmlFor="showInactive" className="ml-2 text-sm font-medium text-gray-700">
                  Removida
                </label>
              </div>
            </div>
          </div>
        </section>


        <div className="my-6 rounded-2xl bg-white p-6 shadow-lg border border-gray-100">
          <h3 className="mb-5 text-xl font-bold text-[#E44131]">📊 Resumo Financeiro</h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Total Pago */}
            <div className="flex flex-col justify-between gap-2 rounded-xl bg-[#FFF5F4] p-4 shadow-sm border border-[#FFE2DF]">
              <span className="text-sm text-gray-500 font-medium">Total Pago</span>
              <span className="text-2xl font-semibold text-gray-800">{numberToCurrency(totalPaidEnum)}</span>
            </div>

            {/* Total Pendente */}
            <div className="flex flex-col justify-between gap-2 rounded-xl bg-[#FFF5F4] p-4 shadow-sm border border-[#FFE2DF]">
              <span className="text-sm text-gray-500 font-medium">Total Pendente</span>
              <span className="text-2xl font-semibold text-gray-800">{numberToCurrency(totalPendingEnum)}</span>
            </div>

            {/* Total em Atraso */}
            <div className="flex flex-col justify-between gap-2 rounded-xl bg-[#FFF5F4] p-4 shadow-sm border border-[#FFE2DF]">
              <span className="text-sm text-gray-500 font-medium">Total em Atraso</span>
              <span className="text-2xl font-semibold text-gray-800">{numberToCurrency(totalOverdueEnum)}</span>
            </div>
          </div>
        </div>


        {modalPayment &&
          <Modal
            className="dark:text-black"
            size="7xl"
            onClose={() => setModalPayment(false)}
            show={modalPayment}
          >
            <Modal.Header>
              <h2 className="label">Pagamentos</h2>
            </Modal.Header>
            <Modal.Body>
              <GenericTable
                headers={tableHeaders}
                data={payments as unknown as Record<string, unknown>[]}
                renders={{
                  notes: (notes, data) =>
                    withInactiveStyle(notes, data),

                  amount: (amount, data) =>
                    withInactiveStyle(numberToCurrency(amount), data),

                  dueDate: (date, data) =>
                    withInactiveStyle(formatDate(date), data),

                  paymentDate: (paymentDate, data) =>
                    withInactiveStyle(paymentDate ? formatDate(paymentDate) : "-", data),

                  status: (status) => {
                    const label = getStatusLabels(status);
                    let className = "";

                    switch (status) {
                      case PAYMENT_STATUS.RECEIVED:
                      case PAYMENT_STATUS.CONFIRMED:
                        className = "text-greenDark font-semibold";
                        break;
                      case PAYMENT_STATUS.PENDING:
                        className = "text-bluePrimary font-semibold";
                        break;
                      case PAYMENT_STATUS.OVERDUE:
                        className = "text-redPrimary font-semibold";
                        break;
                      case PAYMENT_STATUS.Inativo:
                        className = "text-gray";
                        break;
                      default:
                        className = "";
                    }

                    return <span className={className}>{label}</span>;
                  },

                  externalId: (externalId, data) => {
                    const code = externalId.split("_")[1];
                    const link = `https://www.asaas.com/i/${code}`;
                    return withInactiveStyle(
                      <a href={link} target="_blank" rel="noopener noreferrer">link</a>,
                      data
                    );
                  },
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any}
              />

            </Modal.Body>
            <Modal.Footer
              className="modal-footer"
            >
              <button
                className="filter-generic-button"
                onClick={() => setModalPayment(false)}
              >
                Fechar
              </button>
            </Modal.Footer>
          </Modal>
        }

        {!payments.length ? <div> Nenhum pagamento registrado até o momento.</div>
          : (
            partner._id ? (
              <GenericTable
                headers={tableHeaders}
                data={payments as unknown as Record<string, unknown>[]}
                renders={{
                  notes: (notes, data) =>
                    withInactiveStyle(notes, data),

                  amount: (amount, data) =>
                    withInactiveStyle(numberToCurrency(amount), data),

                  dueDate: (date, data) =>
                    withInactiveStyle(formatDate(date), data),

                  paymentDate: (paymentDate, data) =>
                    withInactiveStyle(paymentDate ? formatDate(paymentDate) : "-", data),

                  status: (status) => {
                    const label = getStatusLabels(status);
                    let className = "";

                    switch (status) {
                      case PAYMENT_STATUS.RECEIVED:
                      case PAYMENT_STATUS.CONFIRMED:
                        className = "text-greenDark font-semibold";
                        break;
                      case PAYMENT_STATUS.PENDING:
                        className = "text-bluePrimary font-semibold";
                        break;
                      case PAYMENT_STATUS.OVERDUE:
                        className = "text-redPrimary font-semibold";
                        break;
                      case PAYMENT_STATUS.Inativo:
                        className = "text-gray";
                        break;
                      default:
                        className = "";
                    }

                    return <span className={className}>{label}</span>;
                  },

                  externalId: (externalId, data) => {
                    const code = externalId.split("_")[1];
                    const link = `https://www.asaas.com/i/${code}`;
                    return withInactiveStyle(
                      <a href={link} target="_blank" rel="noopener noreferrer">link</a>,
                      data
                    );
                  },
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any}
                entity="edit-payment"
              />
            ) : <Loading />
          )
        }
      </section>
    </div>
  );
}